import type { Row } from '@tanstack/react-table';
import type { DealEventTct } from '@dealstack/shared/lib/backendClient';
import { useCallback } from 'react';
import { BasicMantineTable } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTable.tsx';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { MantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { DealEventDetailPanel } from '@dealstack/frontend/app/pages/Project/DealEventLog/components/DealEventLogTable/DealEventDetailPanel.tsx';
import { hasDealEventDetail } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogChanges.utils.ts';

interface Props {
	columns: ExtendedColumnDef<DealEventTct>[];
	rows: DealEventTct[];
	dataOperations: MantineTableDataOperations;
	showColumnFilters: boolean;
	onShowColumnFiltersChange: (value: boolean) => void;
	isLoading?: boolean;
}

export const DealEventLogTable = ({
	columns,
	rows,
	dataOperations,
	showColumnFilters,
	onShowColumnFiltersChange,
	isLoading = false,
}: Props) => {
	const canExpand = useCallback((row: Row<DealEventTct>) => hasDealEventDetail(row.original), []);

	const rowProps = useCallback(
		({ row }: { row: Row<DealEventTct> }) => ({
			id: row.id,
			onClick: () => row.getCanExpand() && row.toggleExpanded(),
		}),
		[]
	);

	const detailPanel = useCallback(
		({ row }: { row: Row<DealEventTct> }) => <DealEventDetailPanel event={row.original} />,
		[]
	);

	return (
		<BasicMantineTable<DealEventTct>
			columns={columns}
			data={rows}
			isLoading={isLoading}
			dataOperations={dataOperations}
			manualFiltering
			hideTopToolbar
			showColumnFilters={showColumnFilters}
			onShowColumnFiltersChange={onShowColumnFiltersChange}
			hideBorders
			enableExpanding
			dataTestId="deal-event-log-table"
			getRowCanExpand={canExpand}
			mantineTableBodyRowProps={rowProps}
			renderDetailPanel={detailPanel}
		/>
	);
};
